import { useEffect, useMemo, useRef, useState } from 'react'
import { DragDropProvider, useDraggable, useDroppable } from '@dnd-kit/react'
import { GripVertical, ImagePlus, Layers, Loader2, Plus, Trash2, X } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { useFacilityMutations, useSiteLayout } from '@/hooks/useFacility'
import type { Facility, LayoutBuilding, LayoutFloor, LayoutRoom } from '@/api/facilityApi'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { FacilityBar } from './FacilityBar'
import { nextBuildingCode, roomCount, sortFloors, totalRooms } from '@/lib/siteLayout'

const ROOM = 'layout-room'
const FLOOR = 'layout-floor'

type DragData = { kind: 'room'; roomId: string } | { kind: 'floor'; floorId: string }

type DropData = { kind: 'floor'; floorId: string } | { kind: 'building'; buildingId: string } | { kind: 'unmapped' }

/**
 * Building set-up (Cleaning Visual, z8nrz7f731): add buildings, their floors,
 * and put rooms on floors. Floors and rooms are placed by drag and drop —
 * a room onto a floor, a floor onto a building — and dragged back to the
 * "not placed" lists to take them out again.
 */
export function BuildingSetupDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { data: layout, isLoading } = useSiteLayout(open)
  const mut = useFacilityMutations()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [facilityId, setFacilityId] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // The facility being set up (z8nrz7fw25): its buildings only. A backend that
  // predates facilities sends none, and then every building shows.
  const facilities = useMemo(() => layout?.facilities ?? [], [layout?.facilities])
  const facility = facilities.find((f) => f.id === facilityId) ?? facilities[0] ?? null
  const buildings = (layout?.buildings ?? []).filter((b) => !facility || !b.facilityId || b.facilityId === facility.id)
  const selected = buildings.find((b) => b.id === selectedId) ?? buildings[0]
  // A room is on one floor at most, so Add rooms offers only the unmapped ones:
  // a room already placed is moved deliberately (× off its floor, or dragged),
  // never taken from another building by a tick in a list.
  const unmappedRooms = layout?.unmappedRooms ?? []
  const roomTotal = layout ? totalRooms(layout) : 0
  useEffect(() => {
    if (!open) {
      setAdding(false)
      setError(null)
    }
  }, [open])

  const run = async (fn: () => Promise<unknown>) => {
    setError(null)
    try {
      await fn()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That change was not saved.')
    }
  }

  const onDragEnd = (evt: { operation: { source?: { data: unknown } | null; target?: { data: unknown } | null }; canceled: boolean }) => {
    const { source, target } = evt.operation
    if (evt.canceled || !source || !target) return
    const drag = source.data as DragData
    const drop = target.data as DropData
    if (drag.kind === 'room') {
      if (drop.kind === 'floor') void run(() => mut.setRoomFloor.mutateAsync({ roomId: drag.roomId, floorId: drop.floorId }))
      else if (drop.kind === 'unmapped') void run(() => mut.setRoomFloor.mutateAsync({ roomId: drag.roomId, floorId: null }))
    } else if (drag.kind === 'floor') {
      if (drop.kind === 'building') void run(() => mut.updateFloor.mutateAsync({ id: drag.floorId, buildingId: drop.buildingId }))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-6xl'>
        <DialogHeader>
          <DialogTitle>Building set-up</DialogTitle>
          <DialogDescription>
            Choose the facility, then add each building, all its floors, and the rooms on each floor. Drag rooms onto floors and floors onto buildings.
          </DialogDescription>
        </DialogHeader>
        {error && (
          <p role='alert' className='rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive'>
            {error}
          </p>
        )}
        {isLoading || !layout ? (
          <div className='flex items-center gap-2 py-10 text-sm text-muted-foreground'>
            <Loader2 className='h-4 w-4 animate-spin' /> Loading…
          </div>
        ) : (
          <DragDropProvider onDragEnd={onDragEnd}>
            <div className='grid max-h-[70vh] gap-4 overflow-hidden md:grid-cols-[220px_minmax(0,1fr)_240px]'>
              {/* Buildings */}
              <section className='flex min-h-0 flex-col gap-2 overflow-y-auto'>
                <FacilityBar
                  facilities={facilities}
                  selectedId={facility?.id ?? null}
                  onSelect={(id) => {
                    setFacilityId(id)
                    setSelectedId(null)
                  }}
                  buildingCount={buildings.length}
                  run={run}
                />
                <h3 className='text-[11px] font-semibold uppercase tracking-wide text-gray-400'>Buildings</h3>
                {buildings.map((b) => (
                  <BuildingDrop key={b.id} building={b} selected={b.id === selected?.id} onSelect={() => setSelectedId(b.id)} />
                ))}
                {adding ? (
                  <BuildingForm
                    initial={newBuilding(nextBuildingCode(buildings.map((b) => b.code)))}
                    submitLabel='Add building'
                    onCancel={() => setAdding(false)}
                    onSubmit={(v) =>
                      run(async () => {
                        const created = await mut.createBuilding.mutateAsync({ ...v, ...(facility ? { facilityId: facility.id } : {}) })
                        setSelectedId(created.id)
                        setAdding(false)
                      })
                    }
                  />
                ) : (
                  <Button variant='outline' size='sm' className='justify-start' onClick={() => setAdding(true)}>
                    <Plus className='mr-1.5 h-4 w-4' /> Add building
                  </Button>
                )}
              </section>

              {/* The selected building and its floors */}
              <section className='min-h-0 overflow-y-auto rounded-lg border bg-gray-50/50 p-3'>
                {selected ? (
                  <BuildingDetail
                    // Keyed by its saved fields too: the form copies them once, so a
                    // save the server normalised (trimmed, say) shows what was stored.
                    key={[selected.id, selected.code, selected.name, selected.description].join('|')}
                    building={selected}
                    facilities={facilities}
                    run={run}
                    unmappedRooms={unmappedRooms}
                    roomTotal={roomTotal}
                  />
                ) : (
                  <p className='py-10 text-center text-sm text-muted-foreground'>Add a building to start.</p>
                )}
              </section>

              {/* Not placed yet */}
              <section className='flex min-h-0 flex-col gap-3 overflow-y-auto'>
                <UnmappedRooms rooms={layout.unmappedRooms} total={roomTotal} />
              </section>
            </div>
          </DragDropProvider>
        )}
      </DialogContent>
    </Dialog>
  )
}

// ── Buildings ────────────────────────────────────────────────────────────────

function BuildingDrop({ building, selected, onSelect }: { building: LayoutBuilding; selected: boolean; onSelect: () => void }) {
  const { ref, isDropTarget } = useDroppable({
    id: `building:${building.id}`,
    accept: FLOOR,
    data: { kind: 'building', buildingId: building.id } satisfies DropData,
  })
  const rooms = building.floors.reduce((n, f) => n + f.rooms.length, 0)
  return (
    <button
      ref={ref}
      type='button'
      onClick={onSelect}
      className={cn(
        'flex items-center gap-2.5 rounded-lg border bg-white px-3 py-2 text-left transition-colors',
        selected ? 'border-primary ring-1 ring-primary' : 'hover:bg-gray-50',
        isDropTarget && 'border-primary bg-primary/5',
      )}
    >
      {/* Codes run up to 8 characters: the badge widens and the text shrinks to hold them. */}
      <span
        className={cn(
          'flex h-8 min-w-8 max-w-[64px] shrink-0 items-center justify-center rounded border bg-gray-50 px-1 font-bold text-gray-800',
          building.code.length <= 2 ? 'text-sm' : building.code.length <= 4 ? 'text-xs' : 'text-[10px]',
        )}
      >
        {building.code}
      </span>
      <span className='min-w-0'>
        <span className='block truncate text-sm font-semibold'>{building.name}</span>
        <span className='block text-[11px] text-muted-foreground'>
          {count(building.floors.length, 'floor')} · {count(rooms, 'room')}
        </span>
      </span>
    </button>
  )
}

type BuildingValues = { code: string; name: string; description: string }

function BuildingForm({
  initial,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initial: BuildingValues
  submitLabel: string
  onSubmit: (v: { code: string; name: string; description: string | null }) => Promise<void>
  onCancel?: () => void
}) {
  const [v, setV] = useState(initial)
  const [busy, setBusy] = useState(false)
  const valid = v.code.trim() !== '' && v.name.trim() !== ''
  const set = (k: keyof BuildingValues) => (e: React.ChangeEvent<HTMLInputElement>) => setV((p) => ({ ...p, [k]: e.target.value }))
  return (
    <form
      className='space-y-2 rounded-lg border bg-white p-3'
      onSubmit={(e) => {
        e.preventDefault()
        if (!valid) return
        setBusy(true)
        void onSubmit({
          code: v.code.trim(),
          name: v.name.trim(),
          description: v.description.trim() || null,
        }).finally(() => setBusy(false))
      }}
    >
      <div className='grid grid-cols-[64px_minmax(0,1fr)] gap-2'>
        <label className='text-[11px] font-medium text-gray-600'>
          Code
          <Input value={v.code} onChange={set('code')} maxLength={8} className='mt-1 h-8' aria-label='Building code' />
        </label>
        <label className='text-[11px] font-medium text-gray-600'>
          Name
          <Input value={v.name} onChange={set('name')} placeholder='e.g. Building B' className='mt-1 h-8' aria-label='Building name' />
        </label>
      </div>
      <label className='block text-[11px] font-medium text-gray-600'>
        Description
        <Input value={v.description} onChange={set('description')} placeholder='e.g. Cell Therapy Manufacturing' className='mt-1 h-8' aria-label='Building description' />
      </label>
      {!valid && (
        <p className='text-[11px] text-red-600'>
          {v.code.trim() === '' ? 'Enter a code.' : 'Enter a name.'}
        </p>
      )}
      <div className='flex justify-end gap-2 pt-1'>
        {onCancel && (
          <Button type='button' variant='ghost' size='sm' onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type='submit' size='sm' disabled={!valid || busy}>
          {busy ? 'Saving…' : submitLabel}
        </Button>
      </div>
    </form>
  )
}

function BuildingDetail({
  building,
  facilities,
  run,
  unmappedRooms,
  roomTotal,
}: {
  building: LayoutBuilding
  facilities: Facility[]
  run: (fn: () => Promise<unknown>) => Promise<void>
  unmappedRooms: LayoutRoom[]
  roomTotal: number
}) {
  const mut = useFacilityMutations()
  const fileRef = useRef<HTMLInputElement>(null)
  const floors = useMemo(() => sortFloors(building.floors), [building.floors])
  return (
    <div className='space-y-4'>
      <div className='flex items-start gap-4'>
        <div className='min-w-0 flex-1'>
          <BuildingForm
            initial={{
              code: building.code,
              name: building.name,
              description: building.description ?? '',
            }}
            submitLabel='Save building'
            onSubmit={(v) => run(() => mut.updateBuilding.mutateAsync({ id: building.id, ...v }))}
          />
          {/* Moving a building takes its floors and rooms, and every unit in them, along. */}
          {facilities.length > 1 && building.facilityId && (
            <label className='mt-2 flex items-center gap-2 text-[11px] font-medium text-gray-600'>
              In facility
              <Select
                value={building.facilityId}
                onValueChange={(id) => void run(() => mut.updateBuilding.mutateAsync({ id: building.id, facilityId: id }))}
              >
                <SelectTrigger className='h-7 w-56' aria-label='Building facility'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {facilities.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.code} · {f.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          )}
        </div>
        <div className='w-40 shrink-0 space-y-1.5'>
          <p className='text-[11px] font-medium text-gray-600'>Top-view schematic</p>
          {building.schematicUrl ? (
            <img src={building.schematicUrl} alt={`${building.name} schematic`} className='h-24 w-full rounded border bg-white object-contain' />
          ) : (
            <div className='flex h-24 items-center justify-center rounded border border-dashed bg-white text-[11px] text-gray-400'>
              None
            </div>
          )}
          <input
            ref={fileRef}
            type='file'
            accept='image/png,image/jpeg,image/webp,image/svg+xml'
            className='hidden'
            onChange={(e) => {
              const file = e.target.files?.[0]
              e.target.value = ''
              if (file) void run(() => mut.uploadSchematic.mutateAsync({ id: building.id, file }))
            }}
          />
          <div className='flex gap-1'>
            <Button type='button' variant='outline' size='sm' className='h-7 flex-1 px-2 text-[11px]' onClick={() => fileRef.current?.click()}>
              <ImagePlus className='mr-1 h-3.5 w-3.5' /> {building.schematicUrl ? 'Replace' : 'Upload'}
            </Button>
            {building.schematicUrl && (
              <Button
                type='button'
                variant='ghost'
                size='sm'
                className='h-7 px-2'
                aria-label='Remove schematic'
                onClick={() => void run(() => mut.removeSchematic.mutateAsync(building.id))}
              >
                <X className='h-3.5 w-3.5' />
              </Button>
            )}
          </div>
        </div>
      </div>

      <div>
        <div className='mb-2 flex items-center justify-between'>
          <h3 className='text-[11px] font-semibold uppercase tracking-wide text-gray-400'>Floors of {building.name}</h3>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='h-7 text-xs text-destructive hover:text-destructive'
            onClick={() => {
              // A floor only exists inside a building, so its floors go too; rooms are never deleted.
              const floorCount = building.floors.length
              const roomsOn = roomCount(building.floors)
              const what =
                floorCount === 0
                  ? 'It has no floors.'
                  : `Its ${count(floorCount, 'floor')} ${floorCount === 1 ? 'is' : 'are'} deleted too` +
                    (roomsOn > 0 ? `; ${roomsOn === 1 ? 'its room stays' : `their ${roomsOn} rooms stay`}, unmapped, to place again.` : '.')
              if (window.confirm(`Delete ${building.name}? ${what}`)) {
                void run(() => mut.deleteBuilding.mutateAsync(building.id))
              }
            }}
          >
            <Trash2 className='mr-1 h-3.5 w-3.5' /> Delete building
          </Button>
        </div>
        <div className='space-y-2'>
          {floors.length === 0 && <p className='text-xs text-muted-foreground'>No floors yet. Add the building's floors below.</p>}
          {floors.map((f) => (
            <FloorDrop
              key={f.id}
              floor={f}
              run={run}
              unmappedRooms={unmappedRooms}
              roomTotal={roomTotal}
              takenNames={floors.filter((o) => o.id !== f.id).map((o) => o.name)}
            />
          ))}
          <NewFloorButton
            run={run}
            buildingId={building.id}
            nextLevel={(floors[floors.length - 1]?.level ?? 0) + 1}
            takenNames={floors.map((f) => f.name)}
          />
        </div>
      </div>
    </div>
  )
}

// ── Floors ───────────────────────────────────────────────────────────────────

function FloorDrop({
  floor,
  run,
  unmappedRooms,
  roomTotal,
  takenNames,
}: {
  floor: LayoutFloor
  run: (fn: () => Promise<unknown>) => Promise<void>
  /** What Add rooms offers: rooms on no floor yet. */
  unmappedRooms: LayoutRoom[]
  roomTotal: number
  /** The building's other floors: a floor's name is unique within its building. */
  takenNames: string[]
}) {
  const mut = useFacilityMutations()
  const { ref, isDropTarget } = useDroppable({
    id: `floor:${floor.id}`,
    accept: ROOM,
    data: { kind: 'floor', floorId: floor.id } satisfies DropData,
  })
  const [editing, setEditing] = useState(false)
  // Not while its name is being edited: a press in the inputs would start a drag.
  const drag = useDraggable({
    id: `floor-drag:${floor.id}`,
    type: FLOOR,
    disabled: editing,
    data: { kind: 'floor', floorId: floor.id } satisfies DragData,
  })
  const [picking, setPicking] = useState(false)
  const [name, setName] = useState(floor.name)
  const [description, setDescription] = useState(floor.description ?? '')
  const [level, setLevel] = useState(String(floor.level))
  return (
    <div ref={ref} className={cn('rounded-lg border bg-white p-2.5 transition-colors', isDropTarget && 'border-primary bg-primary/5', drag.isDragging && 'opacity-40')}>
      <div className='flex items-center gap-2'>
        <span ref={drag.handleRef} className='cursor-grab text-gray-400' aria-label={`Drag ${floor.name}`} title='Drag onto another building'>
          <GripVertical className='h-4 w-4' />
        </span>
        <span ref={drag.ref} className='flex min-w-0 flex-1 items-center gap-2'>
          <Layers className='h-4 w-4 shrink-0 text-gray-500' />
          {editing ? (
            <span className='grid flex-1 grid-cols-[110px_minmax(0,1fr)_64px] gap-1.5'>
              <label className='text-[10px] font-medium text-gray-500'>
                Name
                <Input value={name} onChange={(e) => setName(e.target.value)} className='mt-0.5 h-7 text-xs' aria-label='Floor name' />
              </label>
              <label className='text-[10px] font-medium text-gray-500'>
                Description
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder='e.g. Cell Processing Suites'
                  className='mt-0.5 h-7 text-xs'
                  aria-label='Floor description'
                />
              </label>
              <label className='text-[10px] font-medium text-gray-500'>
                Storey
                <Input value={level} onChange={(e) => setLevel(e.target.value)} inputMode='numeric' className='mt-0.5 h-7 text-xs' aria-label='Storey' />
              </label>
            </span>
          ) : (
            <span className='min-w-0'>
              <span className='text-sm font-semibold'>{floor.name}</span>
              {floor.description && <span className='ml-2 text-xs text-muted-foreground'>{floor.description}</span>}
              <span className='ml-2 text-[11px] text-gray-400'>{count(floor.rooms.length, 'room')}</span>
            </span>
          )}
        </span>
        {editing ? (
          <>
            <Button
              type='button'
              size='sm'
              className='h-7 self-end px-2 text-xs'
              disabled={!name.trim() || nameTaken(name, takenNames) || !/^-?\d+$/.test(level.trim())}
              onClick={() =>
                void run(async () => {
                  await mut.updateFloor.mutateAsync({ id: floor.id, name: name.trim(), description: description.trim() || null, level: Number(level) })
                  setEditing(false)
                })
              }
            >
              Save
            </Button>
            <Button type='button' variant='ghost' size='sm' className='h-7 self-end px-2 text-xs' onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='h-7 px-2 text-xs'
              onClick={() => setPicking((p) => !p)}
              aria-expanded={picking}
            >
              <Plus className='mr-1 h-3.5 w-3.5' /> Add rooms
            </Button>
            <Button type='button' variant='ghost' size='sm' className='h-7 px-2 text-xs' onClick={() => setEditing(true)}>
              Edit
            </Button>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-7 px-2'
              aria-label={`Delete ${floor.name}`}
              onClick={() => {
                if (window.confirm(`Delete ${floor.name}? Its rooms stay, unmapped.`)) void run(() => mut.deleteFloor.mutateAsync(floor.id))
              }}
            >
              <Trash2 className='h-3.5 w-3.5 text-gray-400' />
            </Button>
          </>
        )}
      </div>

      {editing && nameTaken(name, takenNames) && (
        <p className='mt-1.5 pl-6 text-[11px] text-red-600'>This building already has a floor named “{name.trim()}”.</p>
      )}

      {picking && (
        <AddRoomsPanel
          floorName={floor.name}
          candidates={unmappedRooms}
          anyRooms={roomTotal > 0}
          onCancel={() => setPicking(false)}
          onAdd={(ids) =>
            run(async () => {
              for (const roomId of ids) await mut.setRoomFloor.mutateAsync({ roomId, floorId: floor.id })
              setPicking(false)
            })
          }
        />
      )}

      <div className='mt-2 flex min-h-[34px] flex-wrap gap-1.5 rounded-md border border-dashed border-gray-200 p-1.5'>
        {floor.rooms.length === 0 ? (
          <span className='self-center px-1 text-[11px] text-gray-400'>No rooms yet. Use Add rooms, or drag rooms here.</span>
        ) : (
          floor.rooms.map((r) => (
            <RoomChip key={r.id} room={r} onRemove={() => void run(() => mut.setRoomFloor.mutateAsync({ roomId: r.id, floorId: null }))} />
          ))
        )}
      </div>
    </div>
  )
}

/** Pick rooms for a floor from those on no floor yet. */
function AddRoomsPanel({
  floorName,
  candidates,
  anyRooms,
  onAdd,
  onCancel,
}: {
  floorName: string
  candidates: LayoutRoom[]
  /** Whether the tenant has rooms at all, to word the empty state. */
  anyRooms: boolean
  onAdd: (roomIds: string[]) => Promise<void>
  onCancel: () => void
}) {
  const [q, setQ] = useState('')
  const [picked, setPicked] = useState<Set<string>>(new Set())
  // Only what is still unmapped: another floor may have taken a ticked room.
  const chosen = candidates.filter((r) => picked.has(r.id)).map((r) => r.id)
  const [busy, setBusy] = useState(false)
  const term = q.trim().toLowerCase()
  const shown = candidates.filter((r) => !term || `${r.label} ${r.typeName}`.toLowerCase().includes(term))
  const toggle = (id: string) =>
    setPicked((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  if (candidates.length === 0) {
    return (
      <div className='mt-2 rounded-md border bg-gray-50 p-2.5 text-xs text-muted-foreground'>
        {anyRooms ? (
          <>Every room is already on a floor. To move one here, take it off its floor with × first, or drag it across.</>
        ) : (
          <>
            No rooms yet. Rooms are created on the Room tab with <span className='font-medium text-gray-700'>+ Add Room</span>.
          </>
        )}{' '}
        <button type='button' onClick={onCancel} className='font-medium text-primary hover:underline'>
          Close
        </button>
      </div>
    )
  }
  return (
    <div className='mt-2 space-y-2 rounded-md border bg-gray-50 p-2.5'>
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder='Search unmapped rooms by ID or type…'
        className='h-8 bg-white text-xs'
        aria-label={`Search rooms to add to ${floorName}`}
        autoFocus
      />
      <ul className='max-h-52 space-y-0.5 overflow-y-auto' aria-label='Rooms'>
        {shown.length === 0 && <li className='px-1 py-2 text-xs text-muted-foreground'>No room matches.</li>}
        {shown.map((r) => (
          <li key={r.id}>
            <label className='flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-xs hover:bg-white'>
              <input type='checkbox' checked={picked.has(r.id)} onChange={() => toggle(r.id)} className='h-3.5 w-3.5 accent-primary' />
              <span className='font-mono font-semibold text-gray-800'>{r.label}</span>
              <span className='inline-block h-[10px] w-[10px] border-2 border-red-600' title='Not mapped' aria-hidden />
              <span className='truncate text-gray-500'>{r.typeName}</span>
            </label>
          </li>
        ))}
      </ul>
      <div className='flex items-center justify-end gap-2'>
        <Button type='button' variant='ghost' size='sm' className='h-7 text-xs' onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type='button'
          size='sm'
          className='h-7 text-xs'
          disabled={chosen.length === 0 || busy}
          onClick={() => {
            setBusy(true)
            void onAdd(chosen).finally(() => setBusy(false))
          }}
        >
          {busy ? 'Adding…' : `Add ${chosen.length || ''} ${chosen.length === 1 ? 'room' : 'rooms'} to ${floorName}`.replace('  ', ' ')}
        </Button>
      </div>
    </div>
  )
}

/** A new floor, created inside the building — always with a place to live. */
function NewFloorButton({
  run,
  buildingId,
  nextLevel = 1,
  takenNames,
}: {
  run: (fn: () => Promise<unknown>) => Promise<void>
  buildingId: string
  nextLevel?: number
  /** The building's floors: a new floor's name must be different. */
  takenNames: string[]
}) {
  const mut = useFacilityMutations()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [open, setOpen] = useState(false)
  const taken = nameTaken(name, takenNames)
  // Synchronous: a second Enter can land before `isPending` re-renders.
  const submitting = useRef(false)
  if (!open) {
    return (
      <Button
        type='button'
        variant='outline'
        size='sm'
        className='h-7 justify-start text-xs'
        onClick={() => {
          setName(`Floor ${nextLevel}`)
          setDescription('')
          setOpen(true)
        }}
      >
        <Plus className='mr-1 h-3.5 w-3.5' /> Add floor
      </Button>
    )
  }
  return (
    <form
      className='grid grid-cols-[120px_minmax(0,1fr)_auto] items-end gap-1.5 rounded-lg border bg-white p-2.5'
      onSubmit={(e) => {
        e.preventDefault()
        if (!name.trim() || taken || submitting.current) return
        submitting.current = true
        void run(async () => {
          try {
            await mut.createFloor.mutateAsync({ name: name.trim(), description: description.trim() || null, level: nextLevel, buildingId })
            setOpen(false)
          } finally {
            submitting.current = false
          }
        })
      }}
    >
      <label className='text-[10px] font-medium text-gray-500'>
        Name
        <Input value={name} onChange={(e) => setName(e.target.value)} className='mt-0.5 h-7 text-xs' aria-label='New floor name' autoFocus />
      </label>
      <label className='text-[10px] font-medium text-gray-500'>
        Description <span className='font-normal text-gray-400'>(optional)</span>
        <Input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder='e.g. Cell Processing Suites'
          className='mt-0.5 h-7 text-xs'
          aria-label='New floor description'
        />
      </label>
      <span className='flex gap-1'>
        <Button type='submit' size='sm' className='h-7 px-2 text-xs' disabled={!name.trim() || taken || mut.createFloor.isPending}>
          Add
        </Button>
        <Button type='button' variant='ghost' size='sm' className='h-7 px-2 text-xs' onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </span>
      {taken && <p className='col-span-3 text-[11px] text-red-600'>This building already has a floor named “{name.trim()}”.</p>}
    </form>
  )
}

// ── Rooms ────────────────────────────────────────────────────────────────────

function RoomChip({ room, unmapped = false, onRemove }: { room: LayoutRoom; unmapped?: boolean; onRemove?: () => void }) {
  const { ref, isDragging } = useDraggable({ id: `room:${room.id}`, type: ROOM, data: { kind: 'room', roomId: room.id } satisfies DragData })
  return (
    <span
      ref={ref}
      title={room.typeName}
      className={cn(
        'inline-flex cursor-grab items-center gap-1.5 rounded border bg-white py-1 pl-2 font-mono text-[11px] font-semibold text-gray-700 shadow-sm',
        onRemove ? 'pr-1' : 'pr-2',
        isDragging && 'opacity-40',
      )}
    >
      {room.label}
      {unmapped && <span className='inline-block h-[11px] w-[11px] border-2 border-red-600' aria-hidden />}
      {onRemove && (
        <button
          type='button'
          onPointerDown={(e) => e.stopPropagation()}
          onClick={onRemove}
          aria-label={`Take ${room.label} off this floor`}
          title='Take off this floor'
          className='rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700'
        >
          <X className='h-3 w-3' />
        </button>
      )}
    </span>
  )
}

function UnmappedRooms({ rooms, total }: { rooms: LayoutRoom[]; total: number }) {
  const [q, setQ] = useState('')
  const shown = rooms.filter((r) => `${r.label} ${r.typeName}`.toLowerCase().includes(q.trim().toLowerCase()))
  return (
    <PoolDrop
      kind='unmapped'
      title={`Unmapped rooms (${rooms.length})`}
      hint='Rooms on no floor yet. Drag one onto a floor, or use Add rooms on the floor.'
    >
      {rooms.length > 6 && <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder='Search rooms…' className='h-7 text-xs' aria-label='Search unmapped rooms' />}
      <div className='flex flex-wrap gap-1.5'>
        {total === 0 ? (
          <p className='text-xs text-muted-foreground'>
            No rooms yet. Add them on the Room tab with <span className='font-medium text-gray-700'>+ Add Room</span>, then place them here.
          </p>
        ) : rooms.length === 0 ? (
          <p className='text-xs text-muted-foreground'>Every room is on a floor.</p>
        ) : (
          shown.map((r) => <RoomChip key={r.id} room={r} unmapped />)
        )}
      </div>
    </PoolDrop>
  )
}

function PoolDrop({
  kind,
  title,
  hint,
  children,
}: {
  kind: 'unmapped'
  title: string
  hint: string
  children: React.ReactNode
}) {
  const { ref, isDropTarget } = useDroppable({
    id: `pool:${kind}`,
    accept: ROOM,
    data: { kind } satisfies DropData,
  })
  return (
    <div ref={ref} className={cn('space-y-2 rounded-lg border bg-white p-3 transition-colors', isDropTarget && 'border-primary bg-primary/5')}>
      <div>
        <h3 className='flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500'>
          <span className='inline-block h-[10px] w-[10px] border-2 border-red-600' aria-hidden />
          {title}
        </h3>
        <p className='mt-0.5 text-[11px] leading-snug text-gray-400'>{hint}</p>
      </div>
      {children}
    </div>
  )
}

function count(n: number, noun: string) {
  return `${n} ${noun}${n === 1 ? '' : 's'}`
}

/** A new building's form, ready to save: the next free code and a matching name. */
function newBuilding(code: string): BuildingValues {
  return { code, name: `Building ${code}`, description: '' }
}

/** Floor names are unique within a building, ignoring case and surrounding spaces. */
function nameTaken(name: string, taken: readonly string[]): boolean {
  const n = name.trim().toLowerCase()
  return n !== '' && taken.some((t) => t.trim().toLowerCase() === n)
}
